import { FlexRowSpacedCentered } from '@fox/layout/src/flex';
import { useTheme } from '@mui/material/styles';
import { type CSSProperties, type PropsWithChildren, useEffect } from 'react';
import styled from 'styled-components';
import { LoadingDotsCenter } from '../animated-foxes/index';
import { Box } from '../box/index';
import { Button } from '../button/index';
import { Paper } from '../paper/index';
import { ButtonsRightProps, WizardOptions, WizardStep, WizardValidation, useWizard } from './hooks';
import { WizardStepper } from './wizard-stepper';

export const WizardHeader = styled(Paper)<{ padding?: string }>`
	padding: ${props => (props.padding ? props.padding : '1rem 1rem 0.5rem')};
	height: 6rem;
	justify-content: space-between;
	background-color: ${props => props.theme.palette.background['paper-elevation-1']};
	display: flex;
	align-items: center;
`;

export const WizardBody = styled.div`
	height: calc(100% - 6rem);
	width: 100%;
	padding: 0;
	overflow: auto;
	scrollbar-gutter: stable;
	background-color: ${props => props.theme.palette.background['paper-elevation-0']};
`;

export const SmallStepWrapper = ({ children }: PropsWithChildren) => {
	const theme = useTheme();
	return (
		<Paper
			style={{
				maxWidth: '840px',
				minHeight: `calc(100% - ${theme.spacing(4)} * 2)`,
				padding: '2rem 1.5rem',
				margin: `${theme.spacing(4)} auto`,
				borderRadius: theme.shape['radius-lg']
			}}
			elevation={2}
		>
			<div style={{ maxWidth: '480px', margin: '0 auto' }}>{children}</div>
		</Paper>
	);
};

export const MedStepWrapper = ({ children }: PropsWithChildren) => {
	const theme = useTheme();
	return (
		<Paper
			style={{
				maxWidth: '840px',
				minHeight: `calc(100% - ${theme.spacing(4)} * 2)`,
				padding: '2rem 7.46rem',
				margin: `${theme.spacing(4)} auto`,
				borderRadius: theme.shape['radius-lg']
			}}
			elevation={2}
		>
			{children}
		</Paper>
	);
};

export const MedLargeStepWrapper = ({ children }: PropsWithChildren) => {
	const theme = useTheme();
	return (
		<Paper
			style={{
				maxWidth: '1040px',
				minHeight: `calc(100% - ${theme.spacing(4)} * 2)`,
				padding: '2rem 7.46rem',
				margin: `${theme.spacing(4)} auto`,
				borderRadius: theme.shape['radius-lg']
			}}
			elevation={2}
		>
			{children}
		</Paper>
	);
};

// maxWidth restored to previous value, a temporary fix to have separation between med and large wrappers
export const LargeStepWrapper = ({ children, style }: PropsWithChildren<{ style?: CSSProperties }>) => {
	const theme = useTheme();
	return (
		<Paper
			style={{
				maxWidth: '90%',
				minHeight: `calc(100% - ${theme.spacing(4)} * 2)`,
				padding: '2rem clamp(2rem, 5%, 7.46rem)',
				margin: `${theme.spacing(4)} auto`,
				borderRadius: theme.shape['radius-lg'],
				display: 'flex',
				flexDirection: 'column',
				...style
			}}
			elevation={2}
		>
			{children}
		</Paper>
	);
};

export const WizardH1FontStyle = { fontWeight: 400 };

export const WizardNextButton = ({ handleNextStep, stepValidations, step, visible = true }: ButtonsRightProps) => {
	if (!visible) return <></>;
	return (
		<Button
			variant="primary"
			title="Next"
			size="medium"
			style={{ textTransform: 'none', whiteSpace: 'nowrap' }}
			disabled={stepValidations.length > step && stepValidations[step + 1] == WizardValidation.Disabled}
			onClick={() => handleNextStep?.()}
		>
			Next
		</Button>
	);
};

export const WizardBackButton = ({ hide, handlePrevStep, title }: { hide: boolean; handlePrevStep: () => void; title: string }) => {
	if (hide) return <div />;
	return (
		<Button style={{ whiteSpace: 'nowrap' }} title={title} variant="secondary" size="medium" onClick={() => handlePrevStep()}>
			{title}
		</Button>
	);
};

export const WizardCancelButton = ({ onCancel, step = 0 }: { onCancel: () => void; step?: number }) => {
	const title = step === 0 ? 'Cancel' : 'Close';
	return (
		<Button variant="secondary" size="medium" onClick={onCancel}>
			{title}
		</Button>
	);
};

export const Wizard = <T extends object = {}>({
	steps,
	hideGap,
	onClose,
	...options
}: { steps: WizardStep<T>[]; hideGap?: boolean; onClose?: () => void } & WizardOptions) => {
	const {
		step,
		CurrentStepComponent,
		handleChangeStep,
		handleNextStep,
		handlePrevStep,
		ButtonsLeft,
		ButtonsRight,
		stepVisited,
		stepValidations,
		data
	} = useWizard<T>(steps, options);
	const allValid = !stepValidations.some(v => v !== WizardValidation.Valid);

	// step 0 is always enabled

	const hasPreviousDisabledStep = stepValidations.some((s, sIdx) => s === WizardValidation.Disabled && sIdx < step);
	const stepEnabled =
		step === 0 ||
		(stepValidations[step] !== WizardValidation.Disabled && stepValidations[step] !== WizardValidation.Suppressed && !hasPreviousDisabledStep);

	useEffect(() => {
		// go to the first step if we are not enabled
		if (!stepEnabled) handleChangeStep(0);
	}, [stepEnabled]);
	return (
		<div style={{ height: '100%', maxHeight: '100%' }}>
			<WizardHeader square variant="outlined">
				<Box
					sx={{
						width: '100%',
						display: 'grid',
						gridTemplateColumns: {
							xxs: 'max-content minmax(0, 1fr) max-content',
							md: 'minmax(max-content, 1fr) minmax(250px, 1.5fr) minmax(max-content, 1fr)'
						},
						alignItems: 'center',
						columnGap: 2
					}}
				>
					<Box sx={{ justifySelf: 'start' }}>
						<FlexRowSpacedCentered>
							{ButtonsLeft ? (
								<ButtonsLeft handlePrevStep={handlePrevStep} />
							) : (
								<>
									<WizardBackButton hide={step === 0} handlePrevStep={handlePrevStep} title="Back" />
									{onClose ? <WizardCancelButton step={step} onCancel={onClose} /> : null}
								</>
							)}
						</FlexRowSpacedCentered>
					</Box>
					<Box sx={{ width: '100%', minWidth: 0, alignSelf: 'stretch', position: 'relative' }}>
						<WizardStepper steps={steps} step={step} stepValidations={stepValidations} onStepChange={handleChangeStep} />
					</Box>
					<Box sx={{ justifySelf: 'end' }}>
						<FlexRowSpacedCentered>
							{ButtonsRight ? (
								<ButtonsRight
									step={step}
									handleNextStep={handleNextStep}
									allStepsValid={allValid}
									stepValidations={stepValidations}
								/>
							) : (
								<WizardNextButton
									visible={step < steps.length - 1}
									handleNextStep={handleNextStep}
									stepValidations={stepValidations}
									step={step}
								/>
							)}
						</FlexRowSpacedCentered>
					</Box>
				</Box>
			</WizardHeader>
			{data ? (
				<WizardBody>
					<CurrentStepComponent currentStep={step} nextStep={handleNextStep} stepVisited={stepVisited} />
				</WizardBody>
			) : (
				<LoadingDotsCenter />
			)}
		</div>
	);
};
